import React, { useMemo, useCallback } from 'react';
import { Alert, Collapse } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { usePredictionsData } from '../../../hooks/usePredictionsData';
import { type PredictionsTableRow } from '../../../types/predictions';
import { PredictionsFiltersComponent } from './components/PredictionsFilters';
import { PredictionsTable } from './components/PredictionsTable';
import { useExportPredictionsCsv } from './hooks/useExportPredictionsCsv';

type TableChangeHandler = NonNullable<TableProps<PredictionsTableRow>['onChange']>;

export const PredictionsPage: React.FC = () => {
  const {
    pendingFilters,
    appliedFilters,
    updatePendingFilters,
    applyFilters,
    hasUnappliedChanges,
    tableData,
    loading,
    error,
    shouldPaginate,
    pagination,
    totalCount,
    pageSizeOptions,
    changePage,
    changeSort,
    order,
  } = usePredictionsData();
  const { exportCsv } = useExportPredictionsCsv();

  const pageSizeOptionsAsString = useMemo(() => pageSizeOptions.map((size) => size.toString()), [pageSizeOptions]);

  const tablePaginationConfig = useMemo<false | TablePaginationConfig>(() => {
    if (!shouldPaginate) {
      return false;
    }

    return {
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: totalCount,
      showSizeChanger: true,
      hideOnSinglePage: true,
      pageSizeOptions: pageSizeOptionsAsString,
    };
  }, [shouldPaginate, pagination.current, pagination.pageSize, totalCount, pageSizeOptionsAsString]);

  const handleTableChange = useCallback<TableChangeHandler>(
    (tablePagination, _filters, sorter) => {
      const normalizedSorter = Array.isArray(sorter)
        ? (sorter[0] as SorterResult<PredictionsTableRow> | undefined)
        : ((sorter as SorterResult<PredictionsTableRow>) ?? undefined);

      const sorterColumnKey = (normalizedSorter?.columnKey ?? normalizedSorter?.field) as string | undefined;
      const sorterOrder =
        normalizedSorter?.order === 'ascend' ? 'ASC' : normalizedSorter?.order === 'descend' ? 'DESC' : undefined;

      const sortChanged =
        sorterColumnKey && sorterColumnKey !== 'index'
          ? sorterOrder
            ? sorterColumnKey !== order.by || sorterOrder !== order.direction
            : Boolean(order.by)
          : false;

      if (sortChanged) {
        const nextPage = shouldPaginate ? (tablePagination?.current ?? 1) : undefined;
        const nextPageSize = shouldPaginate ? (tablePagination?.pageSize ?? pagination.pageSize) : undefined;

        if (sorterColumnKey && sorterColumnKey !== 'index' && sorterOrder) {
          changeSort({
            columnKey: sorterColumnKey,
            direction: sorterOrder,
            page: nextPage,
            pageSize: nextPageSize,
          });
        } else {
          changeSort({
            page: nextPage,
            pageSize: nextPageSize,
          });
        }
        return;
      }

      if (shouldPaginate && tablePagination && typeof tablePagination === 'object') {
        const nextPage = tablePagination.current ?? 1;
        const nextPageSize = tablePagination.pageSize ?? pagination.pageSize;

        if (nextPage !== pagination.current || nextPageSize !== pagination.pageSize) {
          changePage(nextPage, nextPageSize);
        }
      }
    },
    [shouldPaginate, changePage, changeSort, pagination.current, pagination.pageSize, order.by, order.direction]
  );

  return (
    <div className="xchange-page">
      <PredictionsFiltersComponent
        filters={pendingFilters}
        onFiltersChange={updatePendingFilters}
        onApplyFilters={applyFilters}
        loading={loading}
        hasUnappliedChanges={hasUnappliedChanges}
        onExportCsv={() =>
          exportCsv({
            filters: appliedFilters,
            order,
            pagination,
            shouldPaginate,
          })
        }
        exportDisabled={!tableData || tableData.length === 0}
      />

      <Collapse
        size="small"
        style={{ marginBottom: '16px' }}
        items={[
          {
            key: 'metrics-guide',
            label: 'Read Metrics Guide',
            children: (
              <div>
                <div>
                  <strong>Precision:</strong> of everything predicted as win, how many were actually wins.
                  <br />
                  Formula: <code>Correctly Predicted Wins / Predicted Wins * 100</code>
                </div>
                <div>
                  <strong>Recall:</strong> of all actual wins, how many were predicted as win.
                  <br />
                  Formula: <code>Correctly Predicted Wins / Actual Wins * 100</code>
                </div>
                <div>
                  <strong>Avg Prediction Score:</strong> model&apos;s average confidence (0 to 1).
                  <br />
                  Formula: <code>Sum(Prediction Scores) / Total Predictions</code>
                </div>
                <div>
                  <strong>Predicted Win Rate:</strong> share of total predictions marked as win.
                  <br />
                  Formula: <code>Predicted Wins / Total Predictions * 100</code>
                </div>
              </div>
            ),
          },
        ]}
      />

      {error && <Alert title="Error" description={error} type="error" showIcon style={{ marginBottom: '16px' }} />}

      <PredictionsTable
        data={tableData}
        loading={loading}
        filters={appliedFilters}
        pagination={tablePaginationConfig}
        onTableChange={handleTableChange}
        sortState={order}
      />
    </div>
  );
};
